import React from 'react';

interface SEOHeadProps {
    title: string;
    description: string;
    canonicalUrl?: string;
    ogType?: 'website' | 'article';
    ogImage?: string;
    structuredData?: object;
}

const SEOHead: React.FC<SEOHeadProps> = ({
    title,
    description,
    canonicalUrl,
    ogType = 'website',
    ogImage,
    structuredData
}) => {
    const baseUrl = 'https://marketeye.in';
    const fullTitle = `${title} | MarketEye`;
    const defaultImage = `${baseUrl}/logo.png`;

    React.useEffect(() => {
        // Update document title
        document.title = fullTitle;

        // Update or create meta tags
        const updateMetaTag = (property: string, content: string, isProperty = false) => {
            const attribute = isProperty ? 'property' : 'name';
            let element = document.querySelector(`meta[${attribute}="${property}"]`);
            if (!element) {
                element = document.createElement('meta');
                element.setAttribute(attribute, property);
                document.head.appendChild(element);
            }
            element.setAttribute('content', content);
        };

        // Basic meta tags
        updateMetaTag('description', description);

        // Open Graph tags
        updateMetaTag('og:type', ogType, true);
        updateMetaTag('og:title', fullTitle, true);
        updateMetaTag('og:description', description, true);
        if (canonicalUrl) updateMetaTag('og:url', `${baseUrl}${canonicalUrl}`, true);
        updateMetaTag('og:image', ogImage || defaultImage, true);
        updateMetaTag('og:site_name', 'MarketEye', true);

        // Twitter Card tags
        updateMetaTag('twitter:card', 'summary_large_image');
        updateMetaTag('twitter:title', fullTitle);
        updateMetaTag('twitter:description', description);
        updateMetaTag('twitter:image', ogImage || defaultImage);

        // Canonical link
        if (canonicalUrl) {
            let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
            if (!canonical) {
                canonical = document.createElement('link');
                canonical.rel = 'canonical';
                document.head.appendChild(canonical);
            }
            canonical.href = `${baseUrl}${canonicalUrl}`;
        }

        // Structured data
        if (structuredData) {
            let script = document.querySelector('script[type="application/ld+json"]');
            if (!script) {
                script = document.createElement('script');
                script.setAttribute('type', 'application/ld+json');
                document.head.appendChild(script);
            }
            script.textContent = JSON.stringify(structuredData);
        }
    }, [title, description, canonicalUrl, ogType, ogImage, structuredData, fullTitle, defaultImage, baseUrl]);

    return null;
};

export default SEOHead;
